import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, MapPin, Instagram, GraduationCap, Calendar, Quote } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
export function Method() {
  return <div className="bg-brand-cream py-24" id="sobre-avril" data-id="element-74">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-75">
        {/* SECCIÓN 5: EL MECANISMO */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="mb-32" data-id="element-76">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-77">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-78">
              Existe una razón técnica y específica por la que las uñas se
              despegan antes de tiempo.
            </h2>
            <p className="text-lg text-brand-charcoal/80" data-id="element-79">
              No es la marca del gel. No es el tipo de uña de tu clienta. No es
              "la humedad del verano". Es una combinación de tres factores que
              muy pocas manicuristas conocen en profundidad:
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-80">
            {[{
            num: '1',
            title: 'Preparación de la uña natural',
            desc: 'El paso más ignorado y el más importante.'
          }, {
            num: '2',
            title: 'Secuencia y tiempos de aplicación',
            desc: 'Un segundo de más o de menos bajo la lámpara cambia todo.'
          }, {
            num: '3',
            title: 'Sellado y acabado correcto',
            desc: 'Donde el 80% de los errores ocurren sin darse cuenta.'
          }].map((step, i) => <motion.div key={i} whileHover={{
            y: -5
          }} className="bg-brand-ivory p-8 rounded-2xl shadow-sm border border-brand-blush/20 text-center relative" data-id="element-81">
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-brand-gold text-white rounded-full flex items-center justify-center font-serif text-xl shadow-md" data-id="element-82">
                  {step.num}
                </div>
                <h3 className="text-xl font-serif text-brand-charcoal mt-4 mb-3" data-id="element-83">
                  {step.title}
                </h3>
                <p className="text-brand-charcoal/70" data-id="element-84">{step.desc}</p>
              </motion.div>)}
          </div>

          <div className="mt-16 text-center max-w-2xl mx-auto" data-id="element-85">
            <p className="text-xl font-serif text-brand-goldDark italic mb-4" data-id="element-86">
              Cuando los tres factores están alineados, el resultado habla solo.
            </p>
            <p className="text-lg text-brand-charcoal font-medium" data-id="element-87">
              Uñas que duran más de 21 días. Sin excusas. Sin "es que mi clienta
              no cuida".
            </p>
            <p className="text-brand-charcoal/80 mt-4" data-id="element-88">
              Esto es lo que Avril lleva años perfeccionando. Y es exactamente
              lo que enseña.
            </p>
          </div>
        </motion.section>

        {/* SECCIÓN 6: QUIÉN ES AVRIL BALBIS */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="mb-32 bg-brand-ivory rounded-3xl overflow-hidden shadow-sm border border-brand-blush/30" data-id="element-89">
          <div className="grid md:grid-cols-2" data-id="element-90">
            <div className="p-10 md:p-16 flex flex-col justify-center" data-id="element-91">
              <h2 className="text-3xl font-serif text-brand-charcoal mb-6" data-id="element-92">
                Avril no empezó con un salón perfecto ni con miles de
                seguidores.
              </h2>
              <div className="space-y-4 text-brand-charcoal/80" data-id="element-93">
                <p data-id="element-94">
                  Empezó con pasión, mucho ensayo, y la determinación de no
                  conformarse con resultados mediocres.
                </p>
                <p data-id="element-95">
                  Hoy, desde su espacio en{' '}
                  <strong data-id="element-96">18 de Julio 1358, Paysandú</strong>, atiende clientas
                  que llevan años con ella. No porque no tengan otras opciones.
                  Sino porque saben que con Avril, las uñas duran, el trato es
                  impecable, y el resultado siempre supera lo que esperaban.
                </p>
                <p data-id="element-97">Pero Avril no se quedó solo en el servicio.</p>
                <p data-id="element-98">
                  Decidió enseñar. Porque entendió que hay muchas mujeres en
                  Uruguay con el potencial para vivir de esto, y que solo
                  necesitan a alguien que les enseñe bien.
                </p>
                <p className="font-medium text-brand-charcoal pt-2" data-id="element-99">
                  Hoy es Manicurista y Educadora, con alumnas formadas que ya
                  trabajan por cuenta propia y construyen su propio camino.
                </p>
                <p className="font-serif text-xl text-brand-gold italic pt-2" data-id="element-100">
                  Ella vive de las uñas. No solo habla de ellas.
                </p>
              </div>
            </div>
            <div className="bg-brand-blush/20 relative min-h-[400px] md:min-h-full flex items-center justify-center p-8 md:p-12" data-id="element-101">
              <video src="https://cdn.magicpatterns.com/uploads/nZotZyT7AQ5LtNMEgHfBRy/instasaved-05-06-2026.mp4" autoPlay muted loop playsInline className="w-full max-w-sm rounded-2xl shadow-xl object-cover" data-id="element-102" />
            </div>
          </div>
        </motion.section>

        {/* SECCIÓN 7: LO QUE OBTENÉS */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-32" data-id="element-103">
          <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal text-center mb-16" data-id="element-104">
            Lo que obtenés
          </h2>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-8 max-w-4xl mx-auto" data-id="element-105">
            {[{
            b: 'Uñas que duran +21 días',
            r: 'Menos retoques, clientas más felices, mejor reputación'
          }, {
            b: 'Técnica profesional desde el primer módulo',
            r: 'Resultados que podés mostrar desde el día uno'
          }, {
            b: 'Formación con alguien que vive de esto',
            r: 'Sin teoría vacía, todo es aplicable de inmediato'
          }, {
            b: 'Confianza para cobrar lo que tu trabajo vale',
            r: 'Porque sabés exactamente lo que estás entregando'
          }, {
            b: 'Comunidad y seguimiento real',
            r: 'No sos un número, sos una alumna'
          }, {
            b: 'Posibilidad de trabajar de forma independiente',
            r: 'Tu horario, tus precios, tu negocio'
          }].map((item, i) => <motion.div key={i} variants={staggerItem} className="flex gap-4 items-start" data-id="element-106">
                <CheckCircle2 className="text-brand-gold w-6 h-6 flex-shrink-0 mt-1" data-id="element-107" />
                <div data-id="element-108">
                  <h4 className="font-medium text-brand-charcoal text-lg" data-id="element-109">
                    {item.b}
                  </h4>
                  <p className="text-brand-charcoal/70 mt-1" data-id="element-110">→ {item.r}</p>
                </div>
              </motion.div>)}
          </div>
        </motion.section>

        {/* SECCIÓN 8: PRUEBA Y CREDIBILIDAD */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} data-id="element-111">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16" data-id="element-112">
            <div className="text-center" data-id="element-113">
              <MapPin className="w-8 h-8 text-brand-gold mx-auto mb-3" data-id="element-114" />
              <p className="text-sm text-brand-charcoal/80" data-id="element-115">
                Presencia real en Paysandú con clientas de larga data
              </p>
            </div>
            <div className="text-center" data-id="element-116">
              <Instagram className="w-8 h-8 text-brand-gold mx-auto mb-3" data-id="element-117" />
              <p className="text-sm text-brand-charcoal/80" data-id="element-118">
                +2.100 seguidoras en Instagram con contenido educativo
              </p>
            </div>
            <div className="text-center" data-id="element-119">
              <GraduationCap className="w-8 h-8 text-brand-gold mx-auto mb-3" data-id="element-120" />
              <p className="text-sm text-brand-charcoal/80" data-id="element-121">
                Alumnas formadas que hoy trabajan de forma independiente
              </p>
            </div>
            <div className="text-center" data-id="element-122">
              <Calendar className="w-8 h-8 text-brand-gold mx-auto mb-3" data-id="element-123" />
              <p className="text-sm text-brand-charcoal/80" data-id="element-124">
                Agenda activa con turnos y cursos disponibles
              </p>
            </div>
          </div>

          <div className="max-w-3xl mx-auto bg-brand-ivory p-10 rounded-2xl border border-brand-gold/20 relative mt-12" data-id="element-125">
            <Quote className="absolute -top-5 -left-5 w-10 h-10 text-brand-gold/40 rotate-180 bg-brand-cream rounded-full p-2" data-id="element-126" />
            <p className="text-2xl font-serif text-brand-charcoal text-center leading-relaxed" data-id="element-127">
              "Mis alumnas no solo aprenden a hacer uñas. Aprenden a tener un
              negocio."
            </p>
            <p className="text-center text-brand-goldDark font-medium mt-4" data-id="element-128">
              — Avril Balbis
            </p>
          </div>
        </motion.section>
      </div>
    </div>;
}